/**
 * ONE STICKY SAVE BAR PER PAGE — nothing on a payment-types screen saves on
 * toggle. Every change joins this bar; a save sends only what changed.
 *
 * Super Admin only: when a REGISTER-level choice is dirty, the bar offers to
 * copy it to the other tills at the location or in the tenant, previews each
 * one with a dry run, and the button names how many tills will be sent to.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { api } from '../../../services/apiService';
import { RubyGemMark } from '../../RubyGemMark';
import { BulkFields, BULK_FIELD_LABEL, describeChange } from './model';
import { explainPaymentError } from './errors';

export type BroadcastScope = 'one' | 'location' | 'tenant';

export interface BulkResult {
  terminalId: string; registerName?: string; locationId?: string; locationName?: string;
  changed: Array<{ field: string; from: unknown; to: unknown }>; skipped?: string | boolean;
}

// ---------------------------------------------------------------------------
// Broadcast state — counts, dry-run preview, the real run
// ---------------------------------------------------------------------------

export function useBroadcast(opts: {
  tenantId: string;
  /** The till being edited, or null when no single source exists. */
  source: { terminalId: string; locationId: string } | null;
  fields: BulkFields | null;
}) {
  const { tenantId, source, fields } = opts;
  const [scope, setScope] = useState<BroadcastScope>('one');
  const [counts, setCounts] = useState<{ location: number; tenant: number } | null>(null);
  const [preview, setPreview] = useState<{ loading: boolean; results: BulkResult[] | null; error: string | null }>(
    { loading: false, results: null, error: null });
  const fieldsKey = JSON.stringify(fields);
  const seq = useRef(0);

  // Till counts for the segment labels — every till except online-sales ones.
  useEffect(() => {
    if (!source) { setCounts(null); return; }
    let live = true;
    api.posTerminals.list(tenantId).then((res: any) => {
      if (!live) return;
      const tills: any[] = (res?.terminals || []).filter((t: any) => t.terminalType !== 'online_sales');
      setCounts({
        location: tills.filter((t) => t.locationId === source.locationId).length,
        tenant: tills.length,
      });
    }).catch(() => { if (live) setCounts(null); });
    return () => { live = false; };
  }, [tenantId, source?.terminalId, source?.locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing broadcastable left → back to this till only.
  useEffect(() => { if (!fields) setScope('one'); }, [fieldsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const bodyFor = useCallback((dryRun: boolean) => ({
    scope: scope === 'tenant' ? 'tenant' as const : 'location' as const,
    ...(scope === 'location' && source ? { locationId: source.locationId } : {}),
    excludeTerminalId: source?.terminalId || '',
    dryRun,
    fields: fields || {},
  }), [scope, source, fields]);

  // Dry run whenever the wider scope or the fields change.
  useEffect(() => {
    if (scope === 'one' || !fields || !source) { setPreview({ loading: false, results: null, error: null }); return; }
    const mine = ++seq.current;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    const t = window.setTimeout(() => {
      api.posNmi.bulkPaymentTypes(tenantId, bodyFor(true))
        .then((res) => { if (mine === seq.current) setPreview({ loading: false, results: res?.results || [], error: null }); })
        .catch((e: any) => {
          if (mine === seq.current) setPreview({ loading: false, results: null, error: explainPaymentError(e, 'Could not preview the other tills.') });
        });
    }, 250);
    return () => window.clearTimeout(t);
  }, [scope, fieldsKey, source?.terminalId, source?.locationId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const willChange = (preview.results || []).filter((r) => !r.skipped && r.changed.length > 0).length;

  const run = useCallback(async (): Promise<BulkResult[]> => {
    const res = await api.posNmi.bulkPaymentTypes(tenantId, bodyFor(false));
    return res?.results || [];
  }, [tenantId, bodyFor]);

  return {
    available: !!fields && !!source,
    scope: fields && source ? scope : 'one' as BroadcastScope,
    setScope, counts, preview, willChange, run,
    fieldLabels: fields ? BULK_FIELD_LABEL(fields) : [],
    reset: () => setScope('one'),
  };
}

export type Broadcast = ReturnType<typeof useBroadcast>;

// ---------------------------------------------------------------------------
// The bar
// ---------------------------------------------------------------------------

export interface SaveBarProps {
  count: number;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  /** Shown in the clean state, e.g. the till's online status. */
  cleanStatus?: React.ReactNode;
  broadcast?: Broadcast | null;
  locationName?: string;
  tenantName?: string;
  /** Location-wide values are dirty — say they reach every till there. */
  locationWide?: boolean;
}

export const SaveBar: React.FC<SaveBarProps> = ({
  count, saving, onSave, onDiscard, cleanStatus, broadcast, locationName, tenantName, locationWide,
}) => {
  if (count === 0 && !saving) {
    return (
      <div data-testid="pt-savebar" className="sticky bottom-3 z-[5] mt-2 flex flex-wrap items-center gap-3 rounded-[14px] border border-stone-200 bg-white px-[18px] py-2.5 text-stone-900">
        <div className="flex-1 text-[13px]">
          All changes saved
          {cleanStatus && <small className="block text-xs text-stone-600">{cleanStatus}</small>}
        </div>
      </div>
    );
  }

  const b = broadcast && broadcast.available ? broadcast : null;
  const wide = b && b.scope !== 'one';
  const loc = locationName || 'this location';
  const ten = tenantName || 'this tenant';
  const seg = (v: BroadcastScope, label: string) => (
    <button
      key={v}
      type="button"
      onClick={() => b?.setScope(v)}
      aria-pressed={b?.scope === v}
      className={`inline-flex min-h-[36px] items-center rounded-full border px-3 text-[13px] text-white ${
        b?.scope === v ? 'border-ruby bg-ruby' : 'border-stone-600 bg-transparent hover:border-stone-400'}`}
    >
      {label}
    </button>
  );
  const otherCount = b?.preview.results?.length ?? 0;
  const label = saving
    ? 'Saving…'
    : wide
      ? `Save & send to ${1 + (b?.willChange || 0)} till${(b?.willChange || 0) ? 's' : ''}`
      : 'Save & send to till';

  return (
    <div data-testid="pt-savebar" className="sticky bottom-3 z-[5] mt-2 flex flex-wrap items-center gap-3 rounded-[14px] bg-stone-900 py-2.5 pl-[18px] pr-3 text-white shadow-[0_10px_30px_rgba(0,0,0,.25)]">
      {b && (
        <div className="mb-1 flex w-full flex-wrap items-center gap-1.5 border-b border-stone-700 pb-2">
          <span className="mr-1 text-xs text-stone-400">Apply {b.fieldLabels.join(', ')} to</span>
          {seg('one', 'This till only')}
          {seg('location', `All ${b.counts?.location ?? '…'} tills at ${loc}`)}
          {seg('tenant', `All ${b.counts?.tenant ?? '…'} tills in ${ten}`)}
        </div>
      )}
      {wide && b && (
        <div className="w-full pb-2 pt-1">
          <div className="mb-1 text-xs text-stone-400">
            Only the settings you changed are copied — everything else on those tills stays as it is.
            {b.preview.results && ` ${b.willChange} of ${otherCount} other tills will change; each gets its own push.`}
          </div>
          {b.preview.loading && (
            <div className="flex items-center gap-2 py-1 text-xs text-stone-400"><RubyGemMark size={14} drawing color="currentColor" /> Checking the other tills…</div>
          )}
          {b.preview.error && (
            <div role="alert" className="flex items-center gap-2 rounded-lg bg-red-950/60 px-2 py-1.5 text-xs text-red-200">
              <AlertTriangle size={13} /> {b.preview.error}
            </div>
          )}
          {!b.preview.loading && (b.preview.results || []).map((r) => (
            <div key={r.terminalId} className="flex flex-wrap gap-x-2.5 border-t border-stone-800 py-1.5 text-xs">
              <b className="min-w-[92px]">{r.registerName || r.terminalId}</b>
              <span className="min-w-[130px] text-stone-400">{r.locationName || r.locationId || ''}</span>
              <span className={r.changed.length && !r.skipped ? 'text-amber-300' : 'text-green-400'}>
                {r.skipped
                  ? (typeof r.skipped === 'string' ? `Skipped — ${explainPaymentError({ code: r.skipped }, r.skipped)}` : 'Skipped')
                  : r.changed.length ? r.changed.map(describeChange).join(' · ') : 'Already matches — no change'}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex-1 text-[13px]">
        {count} unsaved change{count === 1 ? '' : 's'}
        <small className="block text-xs text-stone-400">
          Only these are sent — nothing you didn't touch is rewritten
          {locationWide ? ` · tip and surcharge grants reach every till at ${loc}` : ''}
        </small>
      </div>
      <button
        type="button"
        onClick={onDiscard}
        disabled={saving}
        className="min-h-[44px] rounded-[10px] px-4 font-medium text-white hover:bg-white/10 disabled:opacity-40"
      >
        Discard
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving || (!!wide && (!!b?.preview.loading || !!b?.preview.error))}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-ruby bg-ruby px-4 font-medium text-white hover:bg-ruby-deep disabled:cursor-default disabled:opacity-40"
      >
        {saving && <RubyGemMark size={14} drawing color="currentColor" />}
        {label}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Error banners — one per failed step, never swallowed
// ---------------------------------------------------------------------------

export const ErrorBanners: React.FC<{ errors: string[]; onDismiss: (i: number) => void }> = ({ errors, onDismiss }) => (
  <>
    {errors.map((e, i) => (
      <div key={i} role="alert" className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-800">
        <AlertTriangle size={15} className="mt-0.5 shrink-0" />
        <span className="flex-1">{e}</span>
        <button type="button" aria-label="Dismiss" onClick={() => onDismiss(i)} className="-m-2 grid h-11 w-11 place-items-center rounded-lg text-red-700 hover:bg-red-100">
          <X size={14} />
        </button>
      </div>
    ))}
  </>
);

/** What a finished broadcast did — shown once, dismissible. */
export const BroadcastSummary: React.FC<{ results: BulkResult[]; onDismiss: () => void }> = ({ results, onDismiss }) => {
  const changed = results.filter((r) => !r.skipped && r.changed.length);
  return (
    <div role="status" className="mb-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] text-green-900">
      <div className="flex items-start gap-2">
        <span className="flex-1 font-medium">
          Sent to {changed.length} other till{changed.length === 1 ? '' : 's'}; {results.length - changed.length} already matched or were skipped.
        </span>
        <button type="button" aria-label="Dismiss" onClick={onDismiss} className="-m-2 grid h-11 w-11 place-items-center rounded-lg hover:bg-green-100">
          <X size={14} />
        </button>
      </div>
      {changed.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs">
          {changed.map((r) => (
            <li key={r.terminalId}><b>{r.registerName || r.terminalId}</b> · {r.changed.map(describeChange).join(' · ')}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
